import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'debounce';
import createCircleAnimation from './createCircle';
import {
  setAddress,
  setPopulation,
} from '../../store/actions';
import {
  setPopulation as setPopulationValue,
  setSelectedArea,
} from '../../store/map';
// The fraction of the diagonal map bbox distance used to
// draw the population circle
const CIRCLE_RADIUS_FRACTION = 8;

const Circle = styled.canvas`
  position: absolute;
  width: 100px;
  height: 100px;
  z-index: 9000;
  pointer-events: none;
`;

/**
 * Determines the circle radius from the map boundary.
 * @param {google.Map} map instance
 * @return {number} distance in meters
 */
const determineCircleRadiusFromMap = (map) => {
  const { google } = window;
  const bounds = map.getBounds();

  return google.maps.geometry.spherical.computeDistanceBetween(
      bounds.getNorthEast(),
      bounds.getSouthWest(),
  ) / CIRCLE_RADIUS_FRACTION;
};

/**
 * Handle population circle on map
 */
class PopulationCircle extends Component {
  static propTypes = {
    altitude: PropTypes.number.isRequired,
    coordinate: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    map: PropTypes.object,
    population: PropTypes.number.isRequired,
    selectedArea: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      radius: PropTypes.number.isRequired,
    }),
    updateAddress: PropTypes.func.isRequired,
    updatePopulation: PropTypes.func.isRequired,
    updatePopulationValue: PropTypes.func.isRequired,
    updateSelectedArea: PropTypes.func.isRequired,
  };

  static defaultProps = {
    map: null,
    selectedArea: null,
  };

  /**
   * @param {object} props Properties of the MapUi.
   */
  constructor(props) {
    super(props);

    this.circleRef = React.createRef();
    this.startSelectLocation = this.startSelectLocation.bind(this);
    this.stopCircleAnimation = () => {};
  }

  /**
   * React lifecycle handler.
   * @param {object} nextProps New props of the instance.
   * @return {boolean} Should component update.
   */
  shouldComponentUpdate(nextProps) {
    const {
      altitude,
      map,
      selectedArea,
      population,
    } = this.props;

    if (!map && nextProps.map) {
      const idleListener = nextProps.map.addListener('idle', () => {
        const { google } = window;
        google.maps.event.removeListener(idleListener);

        if (selectedArea !== null) {
          this.startPopulationLookup(selectedArea);
        }
      });

      const cancelPointSelection = () => {
        // this is bound dynamically, don't bother moving it to the event
        // handler below – it won't work
        this.stopCircleAnimation(true);
      };

      nextProps.map.addListener('mousedown', this.startSelectLocation);
      nextProps.map.addListener('mouseup', cancelPointSelection);
      nextProps.map.addListener('dragstart', cancelPointSelection);
    }

    if (nextProps.selectedArea === null) {
      this.clearPopulationArea();
    } else if (nextProps.selectedArea !== selectedArea) {
      this.startPopulationLookup(nextProps.selectedArea);
    } else if (nextProps.altitude !== altitude) {
      this.startPopulationLookup(selectedArea, false);
    }

    if (nextProps.population !== population && nextProps.population >= 0) {
      this.stopCircleAnimation();
      this.showPopulationArea();
    }

    return false;
  }

  /**
   * React lifecycle handler.
   */
  componentWillUnmount() {
    const { map } = this.props;
    const { google } = window;
    google.maps.event.clearListeners(map, 'mousedown');
    google.maps.event.clearListeners(map, 'mouseup');
    google.maps.event.clearListeners(map, 'dragstart');
  }

  /**
   * Update Circle position and update population.
   * @param {SelectedArea} selectedArea the selected area circle
   * @param {boolean} shouldUpdateAddress
   * coordinate and radius
   */
  startPopulationLookup(selectedArea, shouldUpdateAddress = true) {
    const { google } = window;
    const { map, updatePopulation, updateAddress } = this.props;
    const center = { lat: selectedArea.lat, lng: selectedArea.lng };

    this.clearPopulationArea();

    this.populationArea = new google.maps.Circle({
      strokeColor: '#F4907A',
      strokeOpacity: 0.35,
      strokeWeight: 2,
      fillColor: '#A3E3FF',
      fillOpacity: 0.1,
      map,
      center,
      radius: selectedArea.radius,
      clickable: false,
      visible: false,
    });

    updatePopulation(center, selectedArea.radius);

    if (shouldUpdateAddress) {
      updateAddress(center);
    }
  }

  /**
   * Show the population area circle shape
   */
  showPopulationArea() {
    if (this.populationArea != null) {
      this.populationArea.setVisible(true);
    }
  }

  /**
   * Clears the population area circle shape
   */
  clearPopulationArea() {
    if (this.populationArea != null) {
      this.populationArea.setMap(null);
      const { updatePopulationValue } = this.props;
      updatePopulationValue(-1);
    }
  }

  /**
  * Draw the circle startup animation
  * @param {MapEvent} event the map event
  * @return {Promise} when the animation is complete
  */
  drawCircleAnimation(event) {
    const { x, y } = event.pixel;
    const { current } = this.circleRef;
    current.style.left = `${x - current.width / 2}px`;
    current.style.top = `${y - current.height / 2}px`;

    this.stopCircleAnimation();

    return createCircleAnimation(current, (handler) => {
      this.stopCircleAnimation = handler;
    });
  }

  /**
  * Start location select by starting the circle animation.
  * @param {MapEvent} event Google Maps event.
  */
  async startSelectLocation(event) {
    const { map, updateSelectedArea } = this.props;
    const { latLng } = event;

    try {
      await this.drawCircleAnimation(event);
      updateSelectedArea(latLng.toJSON(), determineCircleRadiusFromMap(map));
    } catch {
      // noop
    }
  }

  /**
   * React lifecycle method.
   * @return {jsx} JSX.
   */
  render() {
    return (
      <Circle ref={this.circleRef} width="100" height="100" />
    );
  }
}

export default connect(
    ({ altitudes, map, ui }) => ({
      altitude: altitudes.value,
      coordinate: map.coordinate,
      population: map.population,
      selectedArea: map.selectedArea,
    }),

    (dispatch) => ({
      updateAddress: (coordinate) => dispatch(setAddress(coordinate)),
      updatePopulationValue: (value) => dispatch(setPopulationValue(value)),
      updatePopulation: debounce(
          (coordinate, radius) => dispatch(setPopulation(coordinate, radius)),
          700,
      ),
      updateSelectedArea: (selectedCoord, radius) => dispatch(setSelectedArea({
        radius,
        ...selectedCoord,
      })),
    }),
)(PopulationCircle);
